import * as React from "react";
import { useEffect, useMemo, useCallback } from "react";
import { cn } from "../../../lib/cn";
import { Box, WithReset } from "../../../sudokit/Layout/BoxReset";
import VStack from "../../../sudokit/Layout/VStack";
import { Text } from "../../../sudokit/components/Text";
import InlineSVG from "react-inlinesvg";

type Option = {
  value: string;
  label: string;
  description?: string;
};

type MultiChoiceVStackProps = {
  options: Option[];
  value?: string[];
  onChange?: (values: string[]) => void;
  disabled?: boolean;
  size?: "sm" | "default" | "lg";
  className?: string;
  spacing?: number;
  align?: "stretch" | "flex-start" | "flex-end" | "center" | "baseline";
  justify?: "flex-start" | "flex-end" | "center" | "space-between" | "space-around" | "space-evenly";
  wrap?: boolean;
  noBackground?: boolean;
};

const MultiChoiceVStack: React.FC<MultiChoiceVStackProps> = ({
  className,
  options,
  value = [],
  onChange,
  disabled,
  spacing = 2,
  align,
  justify,
  wrap,
  noBackground = false,
  size = "lg",
  ...props
}) => {

  const sizeStyles = useMemo(
    () => ({
      sm: "h-4 w-4",
      default: "h-5 w-5",
      lg: "h-6 w-6",
    }),
    []
  );

  const labelSizes = useMemo(
    () => ({
      sm: "text-sm",
      default: "text-base",
      lg: "text-lg",
    }),
    []
  );

  const toggleValue = useCallback(
    (optionValue: string) => {
      if (disabled || !onChange) return;
      const isSelected = value.includes(optionValue);
      const next = isSelected ? value.filter((v) => v !== optionValue) : [...value, optionValue];
      onChange(next);
    },
    [disabled, onChange, value]
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const value = (event.target as HTMLElement)?.dataset?.value;

      if (!value) return;
      if (disabled) return;
      if (options.length === 0) return;
      switch (event.key) {
        case " ":
        case "Enter":
          event.preventDefault();
          toggleValue(value);
          break;
        default:
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [disabled, options, toggleValue]);

  return (
    <WithReset>
      <Box
        className={cn("w-full focus:outline-offset-2 focus-visible:outline-[var(--primary-cta)]", className)}
        role="group"
        aria-disabled={disabled || undefined}
      >
        <VStack
          spacing={spacing}
          align={align}
          justify={justify}
          wrap={wrap}
          {...props}
        >
        {options.map((option) => {
          const isSelected = value.includes(option.value);

          return (
            <Box
              as={"button"}
              key={option.value}
              onClick={() => toggleValue(option.value)}
              role="checkbox"
              data-value={option.value}
              aria-checked={isSelected}
              aria-disabled={disabled || undefined}
              disabled={disabled}
              className={cn(
                "flex w-full items-center px-2.5 pb-3 pt-2 duration-150 ease-in-out cursor-pointer rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-app-primary-cta-high-contrast",
                noBackground ? "bg-transparent" : "bg-app-document-background",
                "!border !border-solid transition-all",
                isSelected
                  ? "!border-app-primary-cta-high-contrast text-app-primary-cta-high-contrast cursor-default hover:cursor-default"
                  : cn(
                      "!border-app-grey-2 text-app-grey-0 hover:text-app-grey-0 hover:!border-app-grey-1 active:scale-[0.995] hover:scale-[1.005] cursor-pointer",
                      noBackground ? "hover:bg-transparent" : "hover:bg-app-grey-4"
                    ),
                disabled && (noBackground ? "opacity-50 cursor-not-allowed hover:bg-transparent" : "opacity-50 cursor-not-allowed hover:bg-app-document-background")
              )}
              tabIndex={0}
            >
              <Box className="flex items-center gap-2 w-full">
                <InlineSVG
                  src="/assets/check-selected.svg"
                  className={cn(
                    "transition-colors flex-none",
                    isSelected
                      ? "fill-app-primary-cta-high-contrast [&>path]:fill-app-primary-cta-high-contrast"
                      : "fill-app-grey-1 [&>path]:fill-app-grey-1"
                  )}
                />

                <Box className="flex flex-col">
                  <Box className={cn("text-left", labelSizes[size])}>
                    <Text
                      size="base"
                      weight="medium"
                      color={isSelected ? "primary-cta" : "grey-0"}
                      font="inter"
                    >
                      {option.label}
                    </Text>
                  </Box>
                  {option.description && (
                    <Box className={`text-left font-inter ${isSelected ? "text-app-primary-cta-high-contrast" : "text-[var(--grey-2)]"}`}>
                      <Text
                        size="xs"
                        lineHeight="normal"
                        weight="medium"
                        color={isSelected ? "primary-cta" : "grey-2"}
                        font="inter"
                        className="!text-[13px]"
                      >
                        {option.description}
                      </Text>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          );
        })}
        </VStack>
      </Box>
    </WithReset>
  );
};

export default MultiChoiceVStack;
